import { UniIconButton } from '@/design-system/components/buttons';
import { TextBody, TextCaption, H6, TextTitle, TextSmall } from '@/design-system/components/typography';
import { SxProp, View, useDripsyTheme, Image, ScrollView } from 'dripsy';
import _ from 'lodash';
import React, { useContext, useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { LayoutContext } from 'contexts/LayoutContext';
import BottomSheet, { useBottomSheetDynamicSnapPoints, BottomSheetBackdrop } from '@gorhom/bottom-sheet';
import { RootStackRoutes } from 'types';
import { AccountManagementStackRoutes } from '@/features/account-management/account.navigator';
import { useNavigation } from '@react-navigation/native';
import { CardDetailStackRoutes, CardDetailStackScreenProps } from './type';
import { CardStatus, CardAccountStatus } from '@/features/account-management/enum';
import { formatAmountByDecimal } from '@/features/account-management/utils';
import { UniSwitch } from '@/design-system/components/inputs';
import { useCardSensitiveDetails } from '@/features/account-management/screens/details/hooks/useCard.hook';

export const CardDetailMainNative: React.FC<CardDetailStackScreenProps<CardDetailStackRoutes.Main>> = ({ navigation, route }) => {
  const { theme } = useDripsyTheme();
  const {
    onLayout,
    account,
    tab,
    record,
    funcLockCard,
    funcUnlockCard,
    funcSetCardLimit,
    funcChangeCardPin,
    refresh,
    funcActivateCard,
    funcCancelCard,
    funcSuspendCard,
    funcUnsuspendCard,
    funcUpdateCardPhoneNumberCard
  } = route.params;
  const { t } = useTranslation('account-management');
  const { body, textMb, textTitle, textAtmPin, btnView, btn, modal, topHeader, carouselTextGroup } = sxStyles;

  const layout = useContext(LayoutContext);
  const renderBackdrop = useCallback(
    (props) => <BottomSheetBackdrop {...props} disappearsOnIndex={0} appearsOnIndex={1} pressBehavior={'none'} enableTouchThrough={true} />,
    []
  );
  const { width, height } = layout.dimensions;
  const { animatedHandleHeight, animatedSnapPoints, animatedContentHeight, handleContentLayout } = useBottomSheetDynamicSnapPoints([
    'CONTENT_HEIGHT',
    'CONTENT_HEIGHT'
  ]);

  const accountNavigation = useNavigation();
  const [menu, setMenu] = useState<boolean>(true);
  const BANNER_HEIGHT_RATIO = 0.631034;
  const containerWidth = layout.dimensions.width - 30;

  const [show, showChange] = useState(false);
  const [isEnabled, setIsEnabled] = useState(record?.status == CardStatus.LOCKED ? true : false);
  const viewHight = ![CardStatus.PENDING_ACTIVATION].includes(record?.status) ? height : height >= 525 ? 525 : height;

  const { sensitiveDetail } = useCardSensitiveDetails({ cardId: record?.id! });

  return (
    <View sx={{ width, height, backgroundColor: 'transparent' }}>
      <BottomSheet
        enablePanDownToClose
        index={1}
        snapPoints={animatedSnapPoints}
        handleHeight={animatedHandleHeight}
        contentHeight={animatedContentHeight}
        backdropComponent={renderBackdrop}
        handleComponent={() => <View />}>
        <View sx={modal}>
          <View sx={{ minHeight: viewHight, backgroundColor: '$backgroundPrimary', paddingBottom: '$12' }} onLayout={handleContentLayout}>
            <ScrollView contentContainerStyle={{ paddingTop: '$5' }}>
              <View sx={{ paddingBottom: '$4' }}>
                <TextTitle semiBold sx={{ display: 'flex', justifyContent: 'center' }}>
                  {t('card-account-management::glossary.card-details')}
                </TextTitle>
                <View sx={{ position: 'absolute', right: 0, zIndex: 2 }}>
                  <UniIconButton icon="cross" onPress={() => navigation.getParent()?.goBack()}></UniIconButton>
                </View>
              </View>
              <View sx={body}>
                <View sx={{ width: containerWidth, height: containerWidth * BANNER_HEIGHT_RATIO, gap: '$5' }}>
                  <Image
                    source={record?.product?.imageUrl || require('assets/images/worldcard_blank.png')}
                    sx={{ width: containerWidth, height: containerWidth * BANNER_HEIGHT_RATIO }}
                  />
                  <View sx={{ ...carouselTextGroup, height: containerWidth * BANNER_HEIGHT_RATIO }}>
                    <View sx={{ gap: '$1' }}>
                      <TextCaption sx={{ color: '$white' }}>{record.embossedName}</TextCaption>
                      <H6 bold sx={{ color: '$white' }}>
                        {(show && sensitiveDetail && sensitiveDetail?.cardNumber) || `**** **** **** ${record?.last4}`}
                      </H6>
                    </View>
                    <View sx={{ flexDirection: 'row', gap: '$5' }}>
                      <View sx={{ gap: '$1' }}>
                        <TextSmall sx={{ color: '#DCDCDC' }}>{t('card-account-management::glossary.expiry-date')}</TextSmall>
                        <TextCaption sx={{ color: '$white' }}>
                          {show ? record.expiryMonth : '**'}/
                          {show ? (record.expiryYear || '').substring((record?.expiryYear?.length || 0) - 2 || 0) : '**'}
                        </TextCaption>
                      </View>
                      <View sx={{ gap: '$1' }}>
                        <TextSmall sx={{ color: '#DCDCDC' }}>CVC2</TextSmall>
                        <TextCaption sx={{ color: '$white' }}>{show ? sensitiveDetail && sensitiveDetail?.cvc2 : '***'}</TextCaption>
                      </View>
                    </View>
                  </View>
                  <View sx={{ flexDirection: 'row', gap: '$5', alignItems: 'center', justifyContent: 'center' }}>
                    <View
                      sx={{
                        flexDirection: 'row',
                        justifyContent: 'center',
                        alignItems: 'center',
                        width: '$10',
                        height: '$10',
                        backgroundColor: show ? '$backgroundDropdownActive' : undefined,
                        border: show ? '1px solid transparent' : '1px solid #CCCCCC',
                        borderRadius: 100
                      }}>
                      <UniIconButton
                        icon={show ? 'show' : 'hide'}
                        onPress={(e) => showChange(!show)}
                        options={{ iconStyles: { themeColor: show ? '$secondary' : undefined } }}
                      />
                    </View>
                  </View>
                  <View sx={{ gap: '$2' }}>
                    <View sx={{ padding: '$4', gap: '$1', backgroundColor: '$backgroundSecondary', borderRadius: '$radius2' }}>
                      <TextCaption sx={{ color: '$textSecondary' }}>
                        {t('card-account-management::card-details.text--todays-spending')}
                      </TextCaption>
                      <H6 bold sx={{ color: '$textPrimary' }}>
                        {formatAmountByDecimal({
                          value: record?.availableLimit || 0,
                          suffix: record.currency,
                          decimals: 2
                        })}
                      </H6>
                    </View>
                    <View sx={{ flexDirection: 'row', gap: '$2' }}>
                      <View
                        sx={{
                          width: '50%',
                          padding: '$4',
                          gap: '$1',
                          backgroundColor: '$backgroundSecondary',
                          borderRadius: '$radius2'
                        }}>
                        <TextCaption>{t('card-account-management::card-details.text--daily-purchase-limit')}</TextCaption>
                        <TextBody semiBold sx={{ color: '$textPrimary' }}>
                          {record.dailyPurchaseLimit === null
                            ? '-'
                            : formatAmountByDecimal({
                                value: record?.dailyPurchaseLimit || 0,
                                suffix: record?.currency,
                                decimals: 2
                              })}
                        </TextBody>
                      </View>
                      <View
                        sx={{
                          width: '50%',
                          padding: '$4',
                          gap: '$1',
                          backgroundColor: '$backgroundSecondary',
                          borderRadius: '$radius2'
                        }}>
                        <TextCaption>{t('card-account-management::card-details.text--daily-atm-limit')}</TextCaption>
                        <TextBody semiBold sx={{ color: '$textPrimary' }}>
                          {record.dailyAtmLimit === null
                            ? '-'
                            : formatAmountByDecimal({
                                value: record?.dailyAtmLimit || 0,
                                suffix: record?.currency,
                                decimals: 2
                              })}
                        </TextBody>
                      </View>
                    </View>
                    <View sx={{ flexDirection: 'row', gap: '$2' }}>
                      <View
                        sx={{
                          width: '50%',
                          padding: '$4',
                          gap: '$1',
                          backgroundColor: '$backgroundSecondary',
                          borderRadius: '$radius2'
                        }}>
                        <TextCaption>{t('card-account-management::card-details.text--daily-limit')}</TextCaption>
                        <TextBody semiBold sx={{ color: '$textPrimary' }}>
                          {record.dailyLimit === null
                            ? '-'
                            : formatAmountByDecimal({
                                value: record?.dailyLimit || 0,
                                suffix: record?.currency,
                                decimals: 2
                              })}
                        </TextBody>
                      </View>
                      <View
                        sx={{
                          width: '50%',
                          padding: '$4',
                          gap: '$1',
                          backgroundColor: '$backgroundSecondary',
                          borderRadius: '$radius2'
                        }}>
                        <TextCaption>{t('card-account-management::card-details.text--single-transaction-limit')}</TextCaption>
                        <TextBody semiBold sx={{ color: '$textPrimary' }}>
                          {record.singleTransactionLimit === null
                            ? '-'
                            : formatAmountByDecimal({
                                value: record?.singleTransactionLimit || 0,
                                suffix: record?.currency,
                                decimals: 2
                              })}
                        </TextBody>
                      </View>
                    </View>
                  </View>
                  <View sx={{ paddingX: '$4', pt: '$3', pb: '$5', gap: '$5' }}>
                    {record.status != CardStatus.CANCELLED && record.cardAccountStatus !== CardAccountStatus.FROZEN && (
                      <>
                        {![CardStatus.PENDING_ACTIVATION].includes(record?.status) ? (
                          <>
                            <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center' }}>
                              <View sx={{ gap: '$1', width: '80%' }}>
                                <TextCaption semiBold sx={{ color: '$textPrimary' }}>
                                  {t('card-account-management::card-details.text--lock-card')}
                                </TextCaption>
                                <TextCaption sx={{ color: '$textSecondary' }}>
                                  {t('card-account-management::glossary.lock-credit-card')}
                                </TextCaption>
                              </View>
                              <UniSwitch
                                value={isEnabled}
                                onValueChange={(value) => {
                                  setIsEnabled(value);
                                  navigation.goBack();
                                  accountNavigation.navigate(RootStackRoutes.AccountManagement, {
                                    screen: AccountManagementStackRoutes.LockCard,
                                    params: {
                                      account: account,
                                      tab: tab,
                                      record,
                                      func: record.status == CardStatus.ACTIVE ? funcLockCard : funcUnlockCard,
                                      refresh
                                    }
                                  });
                                }}
                              />
                            </View>
                            <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '$10' }}>
                              <View sx={{ gap: '$1', width: '80%' }}>
                                <TextCaption semiBold sx={{ color: '$textPrimary' }}>
                                  {t('card-account-management::card-details.text--set-card-limit')}
                                </TextCaption>
                                <TextCaption sx={{ color: '$textSecondary' }}>
                                  {t('card-account-management::glossary.set-card-limit')}
                                </TextCaption>
                              </View>
                              <UniIconButton
                                icon="arrowRight"
                                onPress={() => {
                                  navigation.goBack();
                                  accountNavigation.navigate(RootStackRoutes.AccountManagement, {
                                    screen: AccountManagementStackRoutes.SetCardLimit,
                                    params: { account: account, tab: tab, record, func: funcSetCardLimit, refresh }
                                  });
                                }}></UniIconButton>
                            </View>
                            <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '$10' }}>
                              <View sx={{ gap: '$1', width: '80%' }}>
                                <TextCaption semiBold sx={{ color: '$textPrimary' }}>
                                  {t('card-account-management::change-atm-pin.title--change-atm-pin')}
                                </TextCaption>
                                <TextCaption sx={{ color: '$textSecondary' }}>
                                  {t('card-account-management::glossary.update-atm-pin')}
                                </TextCaption>
                              </View>
                              <UniIconButton
                                icon="arrowRight"
                                onPress={() => {
                                  navigation.goBack();
                                  accountNavigation.navigate(RootStackRoutes.AccountManagement, {
                                    screen: AccountManagementStackRoutes.ChangeATMPIN,
                                    params: { account: account, tab: tab, record, func: funcChangeCardPin }
                                  });
                                }}></UniIconButton>
                            </View>
                          </>
                        ) : (
                          <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '$10' }}>
                            <View sx={{ gap: '$1', width: '80%' }}>
                              <TextCaption semiBold sx={{ color: '$textPrimary' }}>
                                {t('credit-card-activate-card::glossary.activate-card')}
                              </TextCaption>
                              <TextCaption sx={{ color: '$textSecondary' }}>
                                {t('credit-card-activate-card::glossary.card-activation')}
                              </TextCaption>
                            </View>
                            <UniIconButton
                              icon="arrowRight"
                              onPress={() => {
                                navigation.goBack();
                                accountNavigation.navigate(RootStackRoutes.AccountManagement, {
                                  screen: AccountManagementStackRoutes.CardActivation,
                                  params: {
                                    account: account,
                                    tab: tab,
                                    record,
                                    func: funcActivateCard,
                                    refresh
                                  }
                                });
                              }}></UniIconButton>
                          </View>
                        )}
                        {[CardStatus.ACTIVE].includes(record?.status) && (
                          <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '$10' }}>
                            <View sx={{ gap: '$1', width: '80%' }}>
                              <TextCaption semiBold sx={{ color: '$textPrimary' }}>
                                {t('credit-card-cancel-card::glossary.cancel-card')}
                              </TextCaption>
                            </View>
                            <UniIconButton
                              icon="arrowRight"
                              onPress={() => {
                                navigation.goBack();
                                accountNavigation.navigate(RootStackRoutes.AccountManagement, {
                                  screen: AccountManagementStackRoutes.CancelCard,
                                  params: {
                                    account,
                                    tab,
                                    record,
                                    func: funcCancelCard,
                                    refresh
                                  }
                                });
                              }}></UniIconButton>
                          </View>
                        )}
                        {[CardStatus.ACTIVE, CardStatus.SUSPENDED].includes(record?.status) && (
                          <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '$10' }}>
                            <View sx={{ gap: '$1', width: '80%' }}>
                              <TextCaption semiBold sx={{ color: '$textPrimary' }}>
                                {record?.status == CardStatus.ACTIVE
                                  ? t('credit-card-suspend-unsuspend-card::glossary.suspend-card')
                                  : t('credit-card-suspend-unsuspend-card::glossary.unsuspend-card')}
                              </TextCaption>
                            </View>
                            <UniIconButton
                              icon="arrowRight"
                              onPress={() => {
                                navigation.goBack();
                                accountNavigation.navigate(RootStackRoutes.AccountManagement, {
                                  screen: AccountManagementStackRoutes.SuspendCard,
                                  params: {
                                    account,
                                    tab,
                                    record,
                                    func: record.status == CardStatus.ACTIVE ? funcSuspendCard : funcUnsuspendCard,
                                    refresh
                                  }
                                });
                              }}></UniIconButton>
                          </View>
                        )}
                        <View sx={{ flexDirection: 'row', justifyContent: 'space-between', alignItems: 'center', gap: '$10' }}>
                          <View sx={{ gap: '$1', width: '80%' }}>
                            <TextCaption semiBold sx={{ color: '$textPrimary' }}>
                              {t('card-account-management::replace-card.title--update-mobile-number')}
                            </TextCaption>
                          </View>
                          <UniIconButton
                            icon="arrowRight"
                            onPress={() => {
                              navigation.goBack();
                              accountNavigation.navigate(RootStackRoutes.AccountManagement, {
                                screen: AccountManagementStackRoutes.UpdateMobileNo,
                                params: {
                                  tab: tab,
                                  record,
                                  func: funcUpdateCardPhoneNumberCard,
                                  refresh
                                }
                              });
                            }}></UniIconButton>
                        </View>
                      </>
                    )}
                  </View>
                </View>
              </View>
            </ScrollView>
          </View>
        </View>
      </BottomSheet>
    </View>
  );
};

const sxStyles: Record<string, SxProp> = {
  modal: {
    width: '100%',
    padding: '$4'
  },
  body: {},
  textMb: {
    marginBottom: 20
  },
  textTitle: {
    marginBottom: 4,
    color: '$textSecondary'
  },
  textAtmPin: {
    fontWeight: 'bolder',
    color: '$textThemeSecondary'
  },
  btnView: {
    flex: 1,
    display: 'grid',
    gap: '$3',
    mt: '$15'
  },
  btn: {
    width: '100%',
    height: 44
  },
  carouselTextGroup: {
    position: 'absolute',
    top: 0,
    left: '$2',
    paddingX: '$4',
    mt: -30,
    paddingBottom: '$5',
    gap: '$5',
    justifyContent: 'flex-end'
  }
};
